import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { CAMPAIGN } from "@/config/campaign";
import { getPhase } from "@/lib/phase";
import { ChickenAvatar } from "@/components/Mascot";

const NAV = [
  { id: "gewinne", label: "Gewinne" },
  { id: "so-gehts", label: "So geht's" },
  { id: "faq", label: "FAQ" },
];

export const Wordmark = ({ dark = false, className = "text-lg" }) => (
  <span className={`font-display font-extrabold leading-none tracking-tight ${className}`} data-testid="brand-logo">
    <span className={dark ? "text-offwhite" : "text-ink"}>CHICKEN</span>
    <span className="text-gold">WINNER</span>
  </span>
);

export const Header = ({ onNavigate }) => {
  const [open, setOpen] = useState(false);
  const phase = getPhase();
  const ctaLabel = phase === "prelaunch" ? `Start am ${CAMPAIGN.launchLabel.replace(" 2026", "")}` : phase === "closed" ? "Teilnahme beendet" : "Jetzt teilnehmen";

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const go = (id) => {
    setOpen(false);
    onNavigate(id);
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-line bg-paper/90 backdrop-blur-md" data-testid="site-header">
      <div className="mx-auto flex h-16 w-full max-w-[1180px] items-center justify-between px-5">
        <button
          onClick={() => go("top")}
          className="flex items-center gap-2.5"
          aria-label="ChickenWinner – zur Startseite"
          data-testid="header-logo"
        >
          <ChickenAvatar className="h-8 w-8" />
          <Wordmark />
        </button>
        <nav className="hidden items-center gap-8 md:flex" aria-label="Hauptnavigation">
          {NAV.map((item) => (
            <button
              key={item.id}
              onClick={() => go(item.id)}
              data-testid={`nav-${item.id}`}
              className="text-sm font-medium text-coal/70 transition-colors duration-200 hover:text-ink"
            >
              {item.label}
            </button>
          ))}
          <button
            onClick={() => go("teilnehmen")}
            data-testid="header-cta-button"
            className="inline-flex h-11 items-center rounded-[14px] bg-gold px-6 text-sm font-bold text-ink transition-[transform,background-color] duration-200 hover:bg-gold-dark active:scale-95"
          >
            {ctaLabel}
          </button>
        </nav>
        <button
          className="flex h-11 w-11 items-center justify-center text-ink md:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Menü schliessen" : "Menü öffnen"}
          aria-expanded={open}
          data-testid="mobile-menu-button"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-line bg-paper md:hidden"
            aria-label="Mobile Navigation"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {NAV.map((item) => (
                <button
                  key={item.id}
                  onClick={() => go(item.id)}
                  data-testid={`mobile-nav-${item.id}`}
                  className="rounded-xl px-3 py-3.5 text-left text-base font-medium text-coal transition-colors duration-200 hover:bg-black/5"
                >
                  {item.label}
                </button>
              ))}
              <button
                onClick={() => go("teilnehmen")}
                data-testid="mobile-header-cta-button"
                className="mt-2 flex h-12 items-center justify-center rounded-[14px] bg-gold text-base font-bold text-ink"
              >
                {ctaLabel}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
